import { Link } from 'react-router'

export default function PricingPlans() {
  return (
    <>
      <aside className='w-full shrink-0 lg:w-36'>
        <h2>Vitess</h2>
        <p>
          MySQL-compatible with horizontal sharding for massive scale. Runs on network-attached storage or PlanetScale
          Metal.
        </p>
        <h2>Neki</h2>
        <p>
          Postgres with horizontal sharding for massive scale. Runs on network-attached storage or PlanetScale Metal.
        </p>
        <h2>PlanetScale Postgres</h2>
        <h3>Single node</h3>
        <p>Development and low-traffic production workloads. Starting at $5/month.</p>
        <h3>High Availability</h3>
        <p>1 primary + 2 replicas across 3 availability zones.</p>
        <h3>Metal</h3>
        <p>High-performance local NVMe storage. Starting at $50/month.</p>
        <h2>Plans</h2>
        <ul>
          <li>
            <Link to='#base'>Base ↓</Link>
          </li>
          <li>
            <Link to='#enterprise'>Enterprise ↓</Link>
          </li>
        </ul>
        <p>
          Learn more in the <a href='/docs/planetscale-plans'>plans documentation</a>.
        </p>
        <p>
          Also available on the{' '}
          <a href='https://aws.amazon.com/marketplace/pp/prodview-luy3krhkpjne4'>AWS Marketplace</a> and{' '}
          <a href='https://console.cloud.google.com/marketplace/product/planetscale-public/planetscale-database'>
            Google Cloud Marketplace
          </a>
          .
        </p>
        <div className='mt-6 mb-3 border bg-blue-50 p-3 font-semibold text-contrast dark:bg-blue-900'>
          <p>Have questions about Enterprise options, plan features, or pricing?</p>
          <p>
            <Link prefetch='intent' to='/contact'>
              Get in touch
            </Link>
          </p>
        </div>
      </aside>
    </>
  )
}

export function PricingPlanDetails() {
  return (
    <>
      <div className='mt-6 grid grid-cols-1 text-left md:grid-cols-2'>
        <div className='m-[-0.5px] border p-3'>
          <h3>Database expertise</h3>
          <p>Augment your team with our database experts, including the majority of the core Vitess maintainers</p>
        </div>
        <div className='m-[-0.5px] border p-3'>
          <h3>Migration assistance</h3>
          <p>Hands-on, proactive assistance in migrating your workload with no downtime</p>
        </div>
        <div className='m-[-0.5px] border p-3'>
          <h3>Slack-based support</h3>
          <p>Private shared Slack channel for fast communication</p>
        </div>
        <div className='m-[-0.5px] border p-3'>
          <h3>More deployment options</h3>
          <p>Multi-tenant, single-tenant, or in your own AWS / GCP account</p>
        </div>
        <div className='m-[-0.5px] border p-3'>
          <h3>Business support included</h3>
          <p>Enhanced response times from support engineers</p>
        </div>
        <div className='m-[-0.5px] border p-3'>
          <h3>Architecture consultation</h3>
          <p>Work with our Solutions team to optimize your database design</p>
        </div>
        <div className='m-[-0.5px] border p-3'>
          <h3>Custom pricing</h3>
          <p>AWS/GCP Marketplace purchase options</p>
        </div>
        <div className='m-[-0.5px] border p-3'>
          <h3>Additional regions</h3>
          <p>Deploy in any AWS or GCP region with at least 3 availability zones</p>
        </div>
      </div>

      <section>
        <div className='mb-2'>
          <h2 id='base'>Base Plan</h2>
          <p>
            The Base plan is what you get when you <a href='https://app.planetscale.com/new'>sign up at PlanetScale</a>.
            No contract or sales interaction required. Pricing is resource-based, so you can upsize and downsize your
            instances as needed.
          </p>
          <h3>Vitess</h3>
          <p>
            MySQL-compatible databases with horizontal sharding. Pricing based on cluster size, VTGate configuration,
            and storage. Metal also available.
          </p>
          <h3>Neki</h3>
          <p>
            Postgres databases with horizontal sharding. Pricing is based on shard size and count, router configuration,
            replicas, storage, backup storage, and network usage. Metal also available.
          </p>
          <h3>PlanetScale Postgres</h3>
          <ul>
            <li>
              <strong>Single node</strong> &mdash; Starting at $5/month for development and low-traffic workloads
            </li>
            <li>
              <strong>Metal</strong> &mdash; Starting at $50/month with local NVMe storage for high-performance needs
            </li>
            <li>
              <strong>High Availability</strong> &mdash; 1 primary + 2 replicas across 3 availability zones
            </li>
          </ul>
          <h3>Pricing factors</h3>
          <p>
            <strong>Pay as you go</strong> &mdash; Pay month to month based on the price in the calculator above.
          </p>
          <p>The main factors that determine your price are:</p>
          <ul>
            <li>
              <strong>Compute</strong> &mdash; Database instances, Neki routers, and Vitess VTGates
            </li>
            <li>
              <strong>Topology</strong> &mdash; The number of shards and replicas
            </li>
            <li>
              <strong>Storage</strong> &mdash; Storage capacity and, where configurable, provisioned IOPS and
              throughput. Storage is included for Metal databases.
            </li>
            <li>
              <strong>Usage</strong> &mdash; Backup storage and network egress
            </li>
            <li>
              <strong>Add-ons</strong> &mdash; Additional development branch hours, SSO, upgraded support
            </li>
          </ul>
          <p>
            For the full list of features and more information, see our{' '}
            <a href='/docs/planetscale-plans'>plans documentation</a>.
          </p>
        </div>

        <hr className='dot-fill my-3' />

        <div className='enterprise'>
          <h2 id='enterprise'>Enterprise</h2>
          <p>
            Our Enterprise plan comes with all of the same platform features you get with the Base plan, but also
            includes upgraded levels of support and additional deployment options:
          </p>
          <ul>
            <li>
              <strong>Bring your own cloud</strong> &mdash; For enterprise companies or those with strict compliance
              policies that require your data to remain in your own cloud, we also offer {}
              <a href='/docs/plans/managed'>PlanetScale Managed</a>. This allows us to deploy PlanetScale into an AWS or
              GCP account that you own and we manage.
            </li>
            <li>
              <strong>Upgraded support</strong> &mdash; All Enterprise plans come with {}
              <a href='/docs/support#business'>Business support</a> and the option to upgrade to Enterprise support. The
              Enterprise support option is only available on Enterprise plans and gives you access to our team’s
              database architecture expertise through a shared Slack channel and bi-weekly meetings. Additionally, 15
              minute response time for P1 tickets.
            </li>
          </ul>
          <h3>PlanetScale Managed pricing model</h3>
          <p>
            If you choose to deploy PlanetScale in your own AWS or GCP account, the required infrastructure is spun up
            in your account, and you are billed by your cloud provider for those infrastructure costs.
          </p>
          <p>
            This allows you to take advantage of the PlanetScale platform (branching, deploy requests, query monitoring,
            etc) in your own private environment.
          </p>
          <p>
            On top of the infrastructure fees that you pay directly to your cloud provider, you will pay a percentage of
            that cost to PlanetScale for the use of PlanetScale, infrastructure management, migration assistance, and
            ongoing support.
          </p>
          <p>
            For companies with existing AWS or GCP agreements, this option sometimes ends up less expensive than running
            on PlanetScale’s infrastructure, as you can take advantage of your AWS or GCP discounts for these
            infrastructure costs.
          </p>
          <h3>When do I need Enterprise?</h3>
          <p>If you need any of the following, Enterprise may be the best solution for you:</p>
          <ul>
            <li>
              Enterprise support &mdash; Shared Slack channel with our team and bi-weekly meetings where you can discuss
              database performance, optimizations, and more.
            </li>
            <li>You need hands-on evaluation and migration assistance</li>
            <li>You need your database deployed in a single-tenant environment</li>
            <li>You need a PCI DSS certified service provider</li>
            <li>
              You need to keep your data in <strong>your own</strong> AWS or GCP account
            </li>
            <li>
              Any other customizations &mdash; Our Enterprise plan offers a lot of flexibility, so if you have a
              requirement that’s not listed here, it’s best to{' '}
              <Link prefetch='intent' to='/contact'>
                reach out
              </Link>{' '}
              and we can see how we can help.
            </li>
          </ul>
        </div>
      </section>
    </>
  )
}
